import { defineStore } from 'pinia';
import { StatsService } from '@/services/stats';
import { ProductService } from '@/services/product';
import moment from 'moment';

interface QmKpi {
  /** Automation KPI of the last 30 days, percent string with 1 decimal (e.g. "42.3") or 0 */
  days30: string | number;
  /** KPI of the 30 days before that (days -60..-30), same format */
  days30Before: string | number;
  /** KPI of the same 30-day window one year ago, same format */
  days30LastYear: string | number;
}

interface QmState {
  /** True while fetchKpis is running */
  isLoading: boolean;
  /** Quality-management KPI comparison values */
  kpi: QmKpi;
  /** Monthly KPI chart points for the last 12 months: { x: month name (de), y: percent } */
  kpisYear: Array<{ x: string; y: string | number }>;
  /** Training data entries of the last 30 days */
  trainingData: any[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/qm.ts` (namespace `qm`). In-memory
 * only — no `vuexPersist` option, matching the legacy module (not listed among the persisted
 * reducers in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy getters `isLoading`, `kpi`, `kpisYear`, `trainingData` were pure identity passthroughs
 *   of the state props of the same name. Pinia does not allow a getter and a state prop to share a
 *   name, so each getter is renamed `<name>Getter` (`isLoadingGetter`, `kpiGetter`,
 *   `kpisYearGetter`, `trainingDataGetter`); consumers can equally read the state prop directly.
 * - Legacy mutations `setValue` and `resetState` had no matching actions — they become same-named
 *   actions here. The three legacy actions (`fetchKpis`, `fetchKpisYear`, `fetchTrainingdata`)
 *   internally called `commit('setValue', ...)`, which now calls the local `setValue` action via
 *   `this.setValue(...)`.
 */
export const useQmStore = defineStore('qm', {
  state: (): QmState => ({
    isLoading: false,
    kpi: {
      days30: 0,
      days30Before: 0,
      days30LastYear: 0,
    },
    kpisYear: [],
    trainingData: [],
  }),
  getters: {
    /** Renamed from legacy getter `isLoading` (collides with the state prop of the same name). */
    isLoadingGetter(state: QmState) {
      return state.isLoading;
    },
    /** Renamed from legacy getter `kpi` (collides with the state prop of the same name). */
    kpiGetter: (state: QmState) => state.kpi,
    /** Renamed from legacy getter `kpisYear` (collides with the state prop of the same name). */
    kpisYearGetter: (state: QmState) => state.kpisYear,
    /** Renamed from legacy getter `trainingData` (collides with the state prop of the same name). */
    trainingDataGetter: (state: QmState) => state.trainingData,
  },
  actions: {
    async fetchKpis({ botId }: { botId: string }) {
      this.setValue({ key: 'isLoading', value: true });
      const value: any = {};
      {
        const dateRange = [moment().add(-30, 'days'), moment()];
        const data: any = await StatsService.getKpi(botId, 'kpi', dateRange);
        value.days30 = data.kpi ? (data.kpi * 100).toFixed(1) : 0;
      }

      {
        const dateRange = [moment().add(-60, 'days'), moment().add(-30, 'days')];
        const data: any = await StatsService.getKpi(botId, 'kpi', dateRange);
        value.days30Before = data.kpi ? (data.kpi * 100).toFixed(1) : 0;
      }

      {
        const dateRange = [moment().add(-385, 'days'), moment().add(-365, 'days')];
        const data: any = await StatsService.getKpi(botId, 'kpi', dateRange);
        value.days30LastYear = data.kpi ? (data.kpi * 100).toFixed(1) : 0;
      }

      this.setValue({ key: 'kpi', value });
      this.setValue({ key: 'isLoading', value: false });
    },

    async fetchKpisYear({ botId }: { botId: string }) {
      const dateRanges: any[] = [];
      let chartData: Array<{ x: string; y: string | number }> = [];

      for (let i = 1; i <= 12; i += 1) {
        const start = moment()
          .add(i * -1, 'month')
          .startOf('month');
        const end = moment()
          .add(i * -1, 'month')
          .endOf('month');
        dateRanges.push([start, end]);
      }

      for (let dateRange of dateRanges) {
        const data: any = await StatsService.getKpi(botId, 'kpi', dateRange);
        chartData.push({
          x: dateRange[0].locale('de').format('MMMM'),
          y: data.kpi ? (data.kpi * 100).toFixed(1) : 0,
        });
      }

      chartData = chartData.reverse();

      this.setValue({ key: 'kpisYear', value: chartData });
    },
    async fetchTrainingdata({ botId }: { botId: string }) {
      const dateRange = [moment().add(-30, 'days'), moment().add(1, 'days')];

      let value = await ProductService.getTrainingdataPeriod(botId, dateRange);
      // value = value.filter(item => item.name !== 'NOISE');
      this.setValue({ key: 'trainingData', value });
    },
    /**
     * Converted from legacy mutation `setValue` (no matching legacy action existed).
     */
    setValue({ key, value }: { key: keyof QmState; value: any }) {
      (this as any)[key] = value;
    },
    /**
     * Converted from legacy mutation `resetState` (no matching legacy action existed).
     */
    resetState() {
      this.kpi = {
        days30: 0,
        days30Before: 0,
        days30LastYear: 0,
      };
      this.kpisYear = [];
      this.trainingData = [];
    },
  },
});
